'use client';

import { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useRouter, usePathname } from 'next/navigation';

import { authApi } from '@/api/auth';
import { useToast } from '@/hooks/use-toast';
import { ROUTES } from '@/lib/constants';
import { SignupData, VerifyOTPResponse } from '@/types/auth';

interface AuthContextType {
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  signup: (data: SignupData) => Promise<void>;
  signupAndLogin: (data: SignupData) => Promise<string | null>; // Returns email on success
  sendOTP: (email: string) => Promise<boolean>;
  verifyOTP: (data: { email: string; otp: string }) => Promise<VerifyOTPResponse | undefined>;
  login: (data: { email: string; otp: string }) => Promise<VerifyOTPResponse | undefined>;
  logout: () => void;
  signupState: {
    isLoading: boolean;
    error: string | null;
  };
  otpState: {
    isLoading: boolean;
    error: string | null;
  };
  verifyState: {
    isLoading: boolean;
    error: string | null;
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const router = useRouter();
  const { toast } = useToast();

  // Initialize auth state
  useEffect(() => {
    const token = Cookies.get('token');
    setIsAuthenticated(!!token);
    setIsInitialized(true);
  }, []);

  // Add these state interfaces to match JobsContext pattern
  const [signupState, setSignupState] = useState({
    isLoading: false,
    error: null as string | null,
  });

  const [otpState, setOtpState] = useState({
    isLoading: false,
    error: null as string | null,
  });

  const [verifyState, setVerifyState] = useState({
    isLoading: false,
    error: null as string | null,
  });

  const signup = async (data: SignupData) => {
    setSignupState({ isLoading: true, error: null });
    try {
      await authApi.signup(data);
      toast({
        title: 'Success',
        description: 'Account created successfully. Please login to continue.',
      });
      router.push(ROUTES.LOGIN);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create account';
      setSignupState({ isLoading: false, error: errorMessage });
      throw error;
    } finally {
      setSignupState(prev => ({ ...prev, isLoading: false }));
    }
  };

  // New combined signup + login flow for seamless onboarding
  const signupAndLogin = async (data: SignupData): Promise<string | null> => {
    setSignupState({ isLoading: true, error: null });
    try {
      // Step 1: Create account
      await authApi.signup(data);
      
      // Step 2: Automatically send OTP to the email
      await authApi.sendOTP(data.employee.email);
      
      toast({
        title: 'Success',
        description: 'Account created! Please check your email for the OTP.',
      });
      
      return data.employee.email; // Return email for verify OTP step
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create account';
      setSignupState({ isLoading: false, error: errorMessage });
      throw error;
    } finally {
      setSignupState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const sendOTP = async (email: string): Promise<boolean> => {
    setOtpState({ isLoading: true, error: null });
    try {
      await authApi.sendOTP(email);
      toast({
        title: 'Success',
        description: 'OTP sent to your email',
      });
      return true;
    } catch (error) {
      console.error('Error sending OTP:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send OTP';
      setOtpState({ isLoading: false, error: errorMessage });
      return false;
    } finally {
      setOtpState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const verifyOTP = async (data: { email: string; otp: string }) => {
    setVerifyState({ isLoading: true, error: null });
    try {
      const response = await authApi.verifyOTP(data);
      // Convert seconds to days for cookie expiration
      const expiresInDays = response.tokenExpiresIn / (60 * 60 * 24);
      Cookies.set('token', response.token, { expires: expiresInDays });
      setIsAuthenticated(true);
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify OTP';
      setVerifyState({ isLoading: false, error: errorMessage });
      throw error;
    } finally {
      setVerifyState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const login = async (data: { email: string; otp: string }) => {
    setVerifyState({ isLoading: true, error: null });
    try {
      const response = await authApi.login(data);
      // Convert seconds to days for cookie expiration
      const expiresInDays = response.tokenExpiresIn / (60 * 60 * 24);
      Cookies.set('token', response.token, { expires: expiresInDays });
      setIsAuthenticated(true);
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to login';
      setVerifyState({ isLoading: false, error: errorMessage });
      throw error;
    } finally {
      setVerifyState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const logout = () => {
    Cookies.remove('token');
    setIsAuthenticated(false);
    router.push(ROUTES.LOGIN);
    toast({
      title: 'Success',
      description: 'Successfully logged out',
    });
  };

  // Clear auth-related validation errors when landing on auth/public routes
  const pathname = usePathname();
  useEffect(() => {
    const shouldClear = pathname === '/' || pathname === '/login' || pathname === '/signup';
    if (shouldClear) {
      setSignupState(prev => ({ ...prev, error: null }));
      setOtpState(prev => ({ ...prev, error: null }));
      setVerifyState(prev => ({ ...prev, error: null }));
    }
  }, [pathname]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isInitialized,
        isLoading: signupState.isLoading || otpState.isLoading || verifyState.isLoading,
        signup,
        signupAndLogin,
        sendOTP,
        verifyOTP,
        login,
        logout,
        signupState,
        otpState,
        verifyState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
